import {
  DOCUMENT_UPLOAD_MAX_FILE_SIZE,
  type SerializableFile,
} from "$lib/util/document-upload"
import {
  ADDRESS_DOCUMENT_TYPE,
  ValidateYup,
  toStringValues,
} from "@tastyworks/tastyworks-api"
import { boolean, mixed, object, string } from "yup"

const fileField = mixed<SerializableFile>().test(
  "max-file-size-test",
  "yup.document.file.max",
  (value) =>
    !value || (value?.size ?? 0) / 1_000_000 < DOCUMENT_UPLOAD_MAX_FILE_SIZE
)

const confirmSchema = object({
  canOptOutOfAddress: boolean().required(),
  shouldDoUpload: boolean().required(),
})

const uploadSchema = object({
  addressDocumentType: string<ADDRESS_DOCUMENT_TYPE | "">()
    .oneOf(["", ...toStringValues(ADDRESS_DOCUMENT_TYPE)])
    .notRequired(),
  id_document: fileField.optional(),
  ssn_document: fileField.optional(),
  address_document: fileField.optional(),
  visa_document: fileField.optional(),
})

export const entityUploadSchema = object({
  entity_agreement: fileField.required("yup.common.mixed.required"),
  proof_of_entity: fileField.required("yup.common.mixed.required"),
})

export const documentUploadConfirmSchema = ValidateYup.buildSchema(
  "documentUploadConfirmSchema",
  confirmSchema
)

export const documentUploadAddressOptOutSchema = ValidateYup.buildSchema(
  "documentUploadAddressOptOutSchema",
  object({
    idDocHasAddress: boolean().required(),
  })
)

export const documentUploadUploadSchema = ValidateYup.buildSchema(
  "documentUploadUploadSchema",
  uploadSchema
)

export const documentUploadSchema = ValidateYup.buildSchema(
  "documentUploadSchema",
  object({
    confirm: documentUploadConfirmSchema,
    addressOptOut: documentUploadAddressOptOutSchema,
    upload: documentUploadUploadSchema,
    entityUpload: entityUploadSchema,
  })
)

if (import.meta.vitest) {
  const { it, expect, describe } = import.meta.vitest

  const { convertFileToSerializable } = await import(
    "$lib/util/document-upload"
  )
  const createSerializable = () =>
    convertFileToSerializable(new File(["foo"], "bar.txt"))

  describe("documentUploadSchema", () => {
    it("validates a valid document upload", async () => {
      const file = await createSerializable()
      const input = {
        confirm: {
          canOptOutOfAddress: true,
          shouldDoUpload: true,
        },
        addressOptOut: {
          idDocHasAddress: true,
        },
        upload: {
          id_document: file,
          ssn_document: file,
          address_document: file,
          visa_document: file,
        },
        entityUpload: {
          entity_agreement: file,
          proof_of_entity: file,
        },
      }
      const result = await documentUploadSchema.validate(input)
      expect(result).toEqual(input)
    })

    it("throws when entityUpload documents are missing", async () => {
      const input = {
        confirm: {
          canOptOutOfAddress: true,
          shouldDoUpload: true,
        },
        addressOptOut: {
          idDocHasAddress: true,
        },
        upload: {},
        entityUpload: {},
      }
      await expect(() => documentUploadSchema.validate(input)).rejects.toThrow()
    })

    it.each([null, undefined, ""])(
      "allows addressDocumentType to be %s",
      async (nullishVal) => {
        const file = await createSerializable()
        const input = {
          confirm: {
            canOptOutOfAddress: false,
            shouldDoUpload: true,
          },
          addressOptOut: {
            idDocHasAddress: false,
          },
          upload: {
            addressDocumentType: nullishVal,
          },
          entityUpload: {
            entity_agreement: file,
            proof_of_entity: file,
          },
        }
        const result = await documentUploadSchema.validate(input)
        expect(result).toEqual(input)
      }
    )

    it("throws when shouldDoUpload is not provided", async () => {
      const input = {
        confirm: {
          canOptOutOfAddress: false,
          shouldDoUpload: undefined,
        },
        addressOptOut: {
          idDocHasAddress: false,
        },
      }
      await expect(() => documentUploadSchema.validate(input)).rejects.toThrow()
    })
  })
  describe("fileField", () => {
    it("allows no value to be provided", async () => {
      const result = await fileField.validate(undefined)
      expect(result).toEqual(undefined)
    })

    it("throws when null value provided", async () => {
      await expect(() => fileField.validate(null)).rejects.toThrow()
    })

    it("throws when size is over the max value", async () => {
      await expect(() =>
        fileField.validate({ size: 15000000 })
      ).rejects.toThrow()
    })

    it("defaults size to 0 when not provided", async () => {
      const result = await fileField.validate({})
      expect(result).toEqual({})
    })
  })
}
